import React from 'react'
import { useIntl } from 'react-intl'

import AddressWithViewMapButton from './Common/AddressWithViewMapButton'
import ConfirmationDetails from './Common/ConfirmationDetails'
import Divider from './Common/Divider'
import ItemTypeAndBookedLabel from './Common/ItemTypeAndBookedLabel'
import PlaceholderImage from './Common/PlaceholderImage'
import StartAndEndDateTime from './Common/StartAndEndDateTime'
import SummaryAdditionalNotes from './Common/SummaryAdditionalNotes'
import SummaryDataItem from './Common/SummaryDataItem'
import SummaryImage from './Common/SummaryImage'
import DefaultSummaryModalFooter from './Common/SummaryModalFooter/DefaultSummaryModalFooter'

import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import useModalCallbacks from 'tripPlanner/hooks/View/useModalCallbacks'
import type { CarRentalItem } from 'tripPlanner/types/tripItem'

interface Props {
  item: CarRentalItem
}

function CarRentalItemSummary(props: Props) {
  const { item } = props
  const intl = useIntl()

  const { closeModal, goBack } = useModalCallbacks()

  const isPickAndDropTheSame =
    item.startPlace.address === item.endPlace?.address

  const pickUpLabel = intl.formatMessage({
    id: '/traKuiViX',
    defaultMessage: 'Pick-up',
    description: 'Title for pick-up date and time section in car rental summary',
  })
  const dropOffLabel = intl.formatMessage({
    id: 'xHg9lTWMpo',
    defaultMessage: 'Drop-off',
    description: 'Title for drop-off date and time section in car rental summary',
  })
  const pickUpDropOffLocationLabel = intl.formatMessage({
    id: 'CNkxhqXZV7',
    defaultMessage: 'Pick-up/Drop-off location',
    description: 'Address label when pick-up and drop-off are at the same location in car rental summary',
  })
  const pickUpLocationLabel = intl.formatMessage({
    id: 'tLS0DhHfgg',
    defaultMessage: 'Pick-up location',
    description: 'Address label for pick-up location in car rental summary',
  })
  const dropOffLocationLabel = intl.formatMessage({
    id: 'IeTXi0n1hs',
    defaultMessage: 'Drop-off location',
    description: 'Address label for drop-off location in car rental summary',
  })

  return (
    <>
      <ModalHeader
        title={item.carType || ''}
        subtitle={item.companyName}
        onCloseButtonClick={closeModal}
        onBackButtonClick={goBack}
      />

      <ModalBody>
        <SummaryImage
          place={item.startPlace}
          placeholder={<PlaceholderImage itemType={item.type} />}
          tripId={item.tripId}
          tripItemId={item.id}
        />
        <ModalContent>
          <VerticalSpacer gap={20}>
            <ItemTypeAndBookedLabel item={item} />
            <StartAndEndDateTime
              startTitle={pickUpLabel}
              endTitle={dropOffLabel}
              item={item}
            />
            {isPickAndDropTheSame && item.startPlace.address && (
              <AddressWithViewMapButton
                item={item}
                addressLabel={pickUpDropOffLocationLabel}
                address={item.startPlace.address}
              />
            )}
            {!isPickAndDropTheSame && item.startPlace.address && (
              <AddressWithViewMapButton
                item={item}
                addressLabel={pickUpLocationLabel}
                address={item.startPlace.address}
              />
            )}
            {!isPickAndDropTheSame && item.endPlace?.address && (
              <AddressWithViewMapButton
                item={item}
                addressLabel={dropOffLocationLabel}
                address={item.endPlace.address}
              />
            )}
            <ConfirmationDetails
              isBooked={item.isBooked}
              confirmationCode={item.confirmationCode}
              price={item.price}
            />
            <Divider />
            <SummaryDataItem
              title={intl.formatMessage({
                id: 'WZErCIXD1U',
                defaultMessage: 'Website',
                description: 'Label for website URL in car rental summary',
              })}
              value={item.url}
              url={item.url}
            />
            <SummaryDataItem
              title={intl.formatMessage({
                id: 'm7NpKyu1Y2',
                defaultMessage: 'Phone number',
                description: 'Label for phone number in car rental summary',
              })}
              value={item.phone}
              url={`tel:${item.phone}`}
            />
            <SummaryAdditionalNotes notes={item.notes} />
          </VerticalSpacer>
        </ModalContent>
      </ModalBody>

      <DefaultSummaryModalFooter item={item} />
    </>
  )
}

export default CarRentalItemSummary
